import React, { useEffect, useMemo, useState } from 'react';
import { ccmApi } from '../lib/api';
import { copyFor, modeLabel } from '../lib/note-expander-v2';
import {
  SECTION_LABELS, REMOVE_REASONS, CUT_WORDS, canWrite, buildTree, highlightCounts, untouchedInSection,
  previewLength, spanText, sourceWords, acceptBody, problemTickable, administrationReady,
} from '../lib/note-expander-v2-review';
import { NoteExpanderV2Done } from './note-expander-v2-done';

// Note Expander v2, the review (PR 5-UI-2, O191; DESIGN §7.1; mockups "grounded", "maximal").
//   Grounded  every span carries its source on hover and focus (the server resolved each
//             citation from the frozen bundle); suggestions sit beside the note with Add.
//   Maximal   every inferred span is highlighted: Keep, Edit or Remove each one (or Keep all in a
//             section); Write stays off until none is left, and the server refuses it too.
// Chart items are ticked here; an allergy conflict is written only with "Write it anyway"
// (acknowledgeAllergy). Write to chart is for a run started here (manual). A run started in the
// chart is reviewed here but finished there (O191 addendum).

function SourceTip({ sources }) {
  if (!sources || !sources.length) return null;
  return (
    <span className="ne2-tip" role="tooltip">
      {sources.map((s, i) => <span key={i} className="ne2-tip-line">{sourceWords(s)}</span>)}
    </span>
  );
}

// initial: a GET /v2/runs/:id response already in hand (the static render test passes one); without
// it the run is fetched.
export function NoteExpanderV2Review({ runId, copy, patientName, patientElationId, onClose, initial = null }) {
  const [data, setData] = useState(initial); // GET /v2/runs/:id
  const [spans, setSpans] = useState(() => (initial && initial.revision && initial.revision.spans) || []);
  const [itemsDesc, setItemsDesc] = useState(null); // GET /v2/revisions/:id/items
  const [loadError, setLoadError] = useState('');
  const [added, setAdded] = useState(() => new Set());
  const [problemTicks, setProblemTicks] = useState(() => new Map());
  const [adminTicks, setAdminTicks] = useState(() => new Map());
  const [active, setActive] = useState(null); // the highlight whose actions are open
  const [editing, setEditing] = useState(null); // { id, text }
  const [removing, setRemoving] = useState(null); // { id, reason }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null); // { revisionId, outbox, itemLabels }

  useEffect(() => {
    let live = true;
    const withItems = (r) => {
      const has = r.items && ((r.items.problems || []).length || (r.items.administrations || []).length);
      if (r.revision && !r.revision.purged && has && canWrite(r.run)) {
        ccmApi.ne2Items(r.revision.id).then((d) => { if (live) setItemsDesc(d); }).catch(() => { if (live) setItemsDesc({ error: true }); });
      }
    };
    if (initial) withItems(initial);
    else {
      ccmApi.ne2Run(runId).then((r) => {
        if (!live) return;
        setData(r);
        setSpans((r.revision && r.revision.spans) || []);
        withItems(r);
      }).catch(() => { if (live) setLoadError('This expansion could not be loaded. Go back and try again.'); });
    }
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  const run = data && data.run;
  const revision = data && data.revision;
  const writable = canWrite(run) && revision && revision.status === 'ai_draft_ready' && !revision.purged;
  const tree = useMemo(() => buildTree(spans), [spans]);
  const counts = highlightCounts(spans);
  const isMaximal = run && run.mode === 'maximal';
  const limit = (data && data.limits && data.limits.noteChars) || 4800;
  const length = revision ? previewLength(revision.renderedText, spans, revision.suggestions, [...added]) : 0;

  async function touch(spanId, body) {
    // Optimistic for this one span; a refusal restores only it (Keep all touches several in turn).
    const was = spans.find((s) => s.id === spanId);
    setSpans((prev) => prev.map((s) => (s.id === spanId ? { ...s, touch: body.touch, editedText: body.editedText || null, touchReason: body.reason || null } : s)));
    setActive(null); setEditing(null); setRemoving(null); setError('');
    try {
      await ccmApi.ne2Touch(revision.id, { spanId, ...body });
    } catch (e) {
      if (was) setSpans((prev) => prev.map((s) => (s.id === spanId ? was : s)));
      setError(e && e.status === 409 ? (copyFor(copy, 'accept', 'not_open') || 'This expansion can no longer be changed.') : 'That change was not saved. Try again.');
    }
  }

  async function keepAll(section) {
    for (const id of untouchedInSection(spans, section)) {
      // eslint-disable-next-line no-await-in-loop
      await touch(id, { touch: 'kept' });
    }
  }

  async function discard() {
    setBusy(true); setError('');
    try {
      await ccmApi.ne2Discard(revision.id);
      onClose();
    } catch (e) {
      setError('This expansion could not be discarded. Try again.');
      setBusy(false);
    }
  }

  function itemLabels() {
    const out = { problem: [], immunization: [], medication: [] };
    const desc = itemsDesc && !itemsDesc.error ? itemsDesc : { problems: [], administrations: [] };
    for (const p of desc.problems || []) if (problemTicks.has(p.index)) out.problem.push(p.icd10 ? `${p.term} (${p.icd10})` : p.term);
    for (const a of desc.administrations || []) {
      if (adminTicks.has(a.index)) out[a.isVaccine ? 'immunization' : 'medication'].push([a.drug, a.dose].filter(Boolean).join(' '));
    }
    return out;
  }

  async function write() {
    setBusy(true); setError('');
    try {
      const r = await ccmApi.ne2Accept(revision.id, acceptBody({ added, problems: problemTicks, administrations: adminTicks }));
      setDone({ revisionId: revision.id, outbox: r.outbox || [], itemLabels: itemLabels() });
    } catch (e) {
      const b = (e && e.body) || {};
      if (b.reason === 'item_refused') setError(copyFor(copy, 'item', b.why) || 'A chart item cannot be written as ticked.');
      else if (b.reason === 'highlights_untouched') setError(`${copyFor(copy, 'accept', 'highlights_untouched') || 'Every highlight must be kept, edited or removed first.'}${b.open ? ` (${b.open} left)` : ''}`);
      else setError(copyFor(copy, 'accept', b.reason) || 'The note could not be written. Try again.');
      setBusy(false);
    }
  }

  if (done) {
    return (
      <NoteExpanderV2Done revisionId={done.revisionId} initialOutbox={done.outbox} itemLabels={done.itemLabels}
                         copy={copy} patientElationId={patientElationId} onBack={onClose} />
    );
  }

  const suggestions = (revision && revision.suggestions) || [];
  const desc = itemsDesc && !itemsDesc.error ? itemsDesc : null;
  // A ticked administration with an allergy conflict needs "Write it anyway" (the server refuses
  // it otherwise, allergy_conflict).
  const allergyBlocked = !!desc && (desc.administrations || []).some((a) => adminTicks.has(a.index) && !administrationReady(a, adminTicks.get(a.index)));

  return (
    <div className="ne2-review">
      <div className="ne2-review-head">
        <button type="button" className="btn ghost sm" onClick={onClose}>← Back to inbox</button>
        <h2 className="ne2-review-title">{patientName || 'Patient'}</h2>
        {run && <span className="ne2-mode-chip">{modeLabel(run.mode)}</span>}
      </div>

      {loadError && <div className="ne-error" role="alert">{loadError}</div>}
      {!data && !loadError && <div className="ne-empty">Loading…</div>}
      {data && !revision && <div className="ne-empty">This expansion has no note to review.</div>}
      {revision && revision.purged && (
        <div className="ne2-notice" role="status"><p>The expanded text was removed after 90 days, as the retention rule requires. Nothing else changed.</p></div>
      )}
      {revision && !revision.purged && run && !canWrite(run) && (
        <div className="ne2-notice" role="status"><p>Started in the chart. Review it here; finish it in Elation with the extension.</p></div>
      )}
      {revision && !revision.purged && run && canWrite(run) && revision.status !== 'ai_draft_ready' && (
        <div className="ne2-notice" role="status">
          <p>This expansion was already {revision.status === 'discarded' ? 'discarded' : 'accepted'}. It is shown as it was.</p>
          {revision.status !== 'discarded' && (
            <div className="ne2-notice-actions">
              <button type="button" className="btn sm" onClick={() => setDone({ revisionId: revision.id, outbox: [], itemLabels: null })}>See what was written</button>
            </div>
          )}
        </div>
      )}

      {revision && !revision.purged && (
        <div className="ne2-review-body">
          <div className="ne2-note">
            {isMaximal && (
              <div className="ne2-hl-counter" role="status" aria-live="polite">
                {counts.open} of {counts.total} highlight{counts.total === 1 ? '' : 's'} left
              </div>
            )}
            {tree.map((sec) => (
              <section key={sec.section} className="ne2-note-section">
                <div className="ne2-note-section-head">
                  <h4>{sec.label}</h4>
                  {isMaximal && writable && untouchedInSection(spans, sec.section).length > 0 && (
                    <button type="button" className="btn ghost sm" onClick={() => keepAll(sec.section)}>Keep all in this section</button>
                  )}
                </div>
                {sec.blocks.map((block) => {
                  const activeHere = block.spans.find((s) => s.id === active);
                  return (
                    <div key={block.blockSeq} className="ne2-note-block">
                      <p>
                        {block.heading && <strong className="ne2-note-heading">{block.heading}: </strong>}
                        {block.spans.map((s) => {
                          const text = spanText(s);
                          if (s.tag === 'inferred' && isMaximal) {
                            // An inline control (not a <button>, which cannot wrap with the sentence):
                            // focusable, opened with Enter or Space.
                            const state = s.touch || 'open';
                            const toggle = () => setActive(active === s.id ? null : s.id);
                            return (
                              <React.Fragment key={s.id}>
                                <span role="button" tabIndex={writable ? 0 : -1} aria-disabled={writable ? undefined : 'true'}
                                      aria-expanded={active === s.id} className={'ne2-hl is-' + state}
                                      onClick={writable ? toggle : undefined}
                                      onKeyDown={writable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } } : undefined}>
                                  {s.touch === 'removed' ? <s>{s.text}</s> : text}
                                </span>{' '}
                              </React.Fragment>
                            );
                          }
                          if (s.touch === 'removed') return null;
                          return (
                            <React.Fragment key={s.id}>
                              <span className={'ne2-span' + (s.sources && s.sources.length ? ' has-src' : '')} tabIndex={s.sources && s.sources.length ? 0 : undefined}>
                                {text}
                                <SourceTip sources={s.sources} />
                              </span>{' '}
                            </React.Fragment>
                          );
                        })}
                      </p>
                      {activeHere && writable && (
                        <HighlightActions span={activeHere} editing={editing} removing={removing}
                                          onEditing={setEditing} onRemoving={setRemoving}
                                          onTouch={(body) => touch(activeHere.id, body)} />
                      )}
                    </div>
                  );
                })}
              </section>
            ))}
            <div className={'ne2-meta' + (length > limit ? ' is-over' : '')}>
              {length.toLocaleString('en-US')} / {limit.toLocaleString('en-US')} characters · copy note under the patient's PCP
              {length > limit && ' · over the limit: remove something before writing'}
            </div>
            {error && <div className="ne2-field-error" role="alert">{error}</div>}
            {writable && (
              <div className="ne2-review-actions">
                <button type="button" className="btn" onClick={discard} disabled={busy}>Discard</button>
                <button type="button" className="btn accent" onClick={write} disabled={busy || counts.open > 0 || allergyBlocked}>
                  {busy ? 'Writing…' : 'Write to chart'}
                </button>
                {isMaximal && counts.open > 0 && <span className="ne2-hint">Touch every highlight first.</span>}
                {allergyBlocked && <span className="ne2-hint">Confirm or untick the item with an allergy on file.</span>}
              </div>
            )}
          </div>

          <aside className="ne2-side">
            {suggestions.length > 0 && (
              <section className="ne2-side-sec">
                <h4>Suggestions · not in the note</h4>
                {suggestions.map((sg, i) => (
                  <div key={i} className="ne2-side-item">
                    <div className="ne2-side-text">“{sg.text}”</div>
                    {writable && (
                      <button type="button" className={'btn sm' + (added.has(i) ? ' accent' : '')} aria-pressed={added.has(i)}
                              onClick={() => setAdded((prev) => { const n = new Set(prev); if (n.has(i)) n.delete(i); else n.add(i); return n; })}>
                        {added.has(i) ? 'Added' : 'Add'}
                      </button>
                    )}
                  </div>
                ))}
              </section>
            )}

            {canWrite(run) && data.items && ((data.items.problems || []).length > 0 || (data.items.administrations || []).length > 0) && (
              <section className="ne2-side-sec">
                <h4>Chart items · tick to write</h4>
                {!desc && !(itemsDesc && itemsDesc.error) && <div className="ne2-hint">Checking codes…</div>}
                {itemsDesc && itemsDesc.error && <div className="ne2-field-error">The chart items could not be checked. Write the note without them, or go back and try again.</div>}
                {desc && (desc.problems || []).map((p) => (
                  <ProblemItem key={'p' + p.index} item={p} writable={writable} choice={problemTicks.get(p.index)}
                               onChange={(c) => setProblemTicks((prev) => { const n = new Map(prev); if (c) n.set(p.index, c); else n.delete(p.index); return n; })} />
                ))}
                {desc && (desc.administrations || []).map((a) => (
                  <AdministrationItem key={'a' + a.index} item={a} writable={writable} choice={adminTicks.get(a.index)}
                                      onChange={(c) => setAdminTicks((prev) => { const n = new Map(prev); if (c) n.set(a.index, c); else n.delete(a.index); return n; })} />
                ))}
              </section>
            )}

            {(revision.cuts || []).length > 0 && (
              <section className="ne2-side-sec">
                <h4>Cut by the safety checks · never written</h4>
                {revision.cuts.map((c, i) => (
                  <div key={i} className="ne2-side-item ne2-cut">
                    {CUT_WORDS[c.group] || 'Removed by a safety check'}{c.section && SECTION_LABELS[c.section] ? ` · ${SECTION_LABELS[c.section]}` : ''}
                  </div>
                ))}
              </section>
            )}

            {(revision.advisory || []).length > 0 && (
              <section className="ne2-side-sec">
                <h4>Advisory check</h4>
                {revision.advisory.map((n, i) => (
                  <div key={i} className="ne2-side-item ne2-advisory">{n.message}</div>
                ))}
              </section>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}

// The open highlight's actions: Keep, Edit (a textarea, Save off while blank) or Remove (an
// optional reason). editing: { id, text } | null · removing: { id, reason } | null
function HighlightActions({ span, editing, removing, onEditing, onRemoving, onTouch }) {
  const isEditing = editing && editing.id === span.id;
  const isRemoving = removing && removing.id === span.id;
  return (
    <div className="ne2-hl-actions">
      {isEditing && (
        <>
          <textarea className="ne2-notes" rows={3} value={editing.text} maxLength={2000} aria-label="Edit this sentence"
                    onChange={(e) => onEditing({ id: span.id, text: e.target.value })} />
          <div className="ne2-hl-buttons">
            <button type="button" className="btn sm accent" disabled={!editing.text.trim()}
                    onClick={() => onTouch({ touch: 'edited', editedText: editing.text })}>Save</button>
            <button type="button" className="btn sm ghost" onClick={() => onEditing(null)}>Cancel</button>
          </div>
        </>
      )}
      {isRemoving && (
        <>
          <div className="ne2-seg" role="radiogroup" aria-label="Why remove it (optional)">
            {REMOVE_REASONS.map(([value, label]) => (
              <button key={value} type="button" role="radio" aria-checked={removing.reason === value}
                      className={'ne2-seg-btn' + (removing.reason === value ? ' is-on' : '')}
                      onClick={() => onRemoving({ id: span.id, reason: removing.reason === value ? null : value })}>{label}</button>
            ))}
          </div>
          <div className="ne2-hl-buttons">
            <button type="button" className="btn sm danger"
                    onClick={() => onTouch(removing.reason ? { touch: 'removed', reason: removing.reason } : { touch: 'removed' })}>Remove</button>
            <button type="button" className="btn sm ghost" onClick={() => onRemoving(null)}>Cancel</button>
          </div>
        </>
      )}
      {!isEditing && !isRemoving && (
        <div className="ne2-hl-buttons">
          <button type="button" className="btn sm accent" onClick={() => onTouch({ touch: 'kept' })}>Keep</button>
          <button type="button" className="btn sm" onClick={() => onEditing({ id: span.id, text: spanText(span) || span.text || '' })}>Edit</button>
          <button type="button" className="btn sm" onClick={() => onRemoving({ id: span.id, reason: null })}>Remove</button>
        </div>
      )}
    </div>
  );
}

function ProblemItem({ item, writable, choice, onChange }) {
  const dx = item.dx || {};
  const label = item.icd10 ? `${item.term} (${item.icd10})` : item.term;
  if (dx.status === 'documented') return <div className="ne2-side-item"><div className="ne2-side-text">Problem: {label}</div><div className="ne2-hint">Already on the problem list.</div></div>;
  if (!problemTickable(item)) return <div className="ne2-side-item"><div className="ne2-side-text">Problem: {label}</div><div className="ne2-hint">No code was found. Add it in Elation if it belongs on the list.</div></div>;
  const options = dx.status === 'options' ? (dx.options || []) : [];
  const picked = choice && choice.snomed;
  return (
    <div className="ne2-side-item">
      <label className="ne2-check">
        <input type="checkbox" disabled={!writable || (dx.status === 'options' && !picked)} checked={!!choice}
               onChange={(e) => onChange(e.target.checked ? (dx.status === 'codeable' ? { snomed: dx.snomed } : { snomed: picked }) : null)} />
        <span>Problem: {dx.status === 'codeable' ? `${dx.description || item.term} (${dx.icd10})` : label}</span>
      </label>
      {dx.status === 'options' && (
        <select className="ne2-select" value={picked || ''} disabled={!writable} aria-label={`Code for ${item.term}`}
                onChange={(e) => onChange(e.target.value ? { snomed: e.target.value } : null)}>
          <option value="">Choose the matching concept…</option>
          {options.map((o) => <option key={o.snomed} value={o.snomed}>{o.name}{o.icd10Preview ? ` (${o.icd10Preview})` : ''}</option>)}
        </select>
      )}
    </div>
  );
}

function AdministrationItem({ item, writable, choice, onChange }) {
  const what = [item.drug, item.dose, item.route].filter(Boolean).join(' ');
  const kind = item.isVaccine ? 'Immunization' : 'Medication given';
  if (item.isVaccine && !item.cvx) return <div className="ne2-side-item"><div className="ne2-side-text">{kind}: {what}</div><div className="ne2-hint">No vaccine code was found, so it is not written. Add it in Elation.</div></div>;
  return (
    <div className="ne2-side-item">
      <label className="ne2-check">
        <input type="checkbox" disabled={!writable} checked={!!choice}
               onChange={(e) => onChange(e.target.checked ? { acknowledgeAllergy: false } : null)} />
        <span>{kind}: {what}</span>
      </label>
      {item.allergyConflict && (
        <div className="ne2-allergy" role="alert">
          <div>This may conflict with an allergy on file.</div>
          {choice && (
            <label className="ne2-check">
              <input type="checkbox" disabled={!writable} checked={!!choice.acknowledgeAllergy}
                     onChange={(e) => onChange({ acknowledgeAllergy: e.target.checked })} />
              <span>Write it anyway</span>
            </label>
          )}
          {choice && !administrationReady(item, choice) && <div className="ne2-hint">Tick “Write it anyway”, or untick the item.</div>}
        </div>
      )}
    </div>
  );
}
